import { connect, useDispatch } from "react-redux";
import Modal from "react-modal";
import {
  Header,
  Paragraph,
  Button,
  LineSeperator,
  ErrorMark,
  NavigationLinkButton
} from "../../components/RegularComponents";
import TopNavigationBar from "../TopNavigationBar";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  withdrawAgentApplication,
  markAgentApplicationDeleted,
  setAssociateBrokerLicenceForUpgrade
} from "../../redux/slice/additionalApplicationsSlice";
import { saveBusinessLicenceApplicationData } from "../../redux/slice/businessLicenceApplicationSlice";
import { fetchApplicationWithId } from "../../redux/slice/bltHomeSlice";
import { bltHomeSelect } from "../../redux/selector/bltHomeSelector";
import { humanReadableDate, combineAddress } from "../../app/utils";
import {
  LICENCE_STATUS_ACTIVE,
  APPLICATION_MODE_NEW,
  // APPLICATION_STATUS_WITHDRAWN_BY_CLIENT,
  // APPLICATION_STATUS_WITHDRAWN_APPROVED,
  APPLICATION_STATUS_DENIED,
  APPLICATION_STATUS_PENDING,
  APPLICATION_STATUS_UNREAD,
  APPLICATION_STATUS_OPENED,
  APPLICATION_STATUS_UNDER_REVIEW,
  ROUTE_PATH,
  APPLICATION_TYPE_MORTGAGE_ASSOCIATE_BROKER_LICENCE
} from "../../app/constants";
import { ERROR_STRING } from "../../app/errors";
import "./AgentDetailsView.styles.scss";
import ApplicationStatusComponent from "../BLT/BLTCommon/ApplicationStatus";
import {
  applicationTypeFromLicenceSubType,
  isMortgageTypeApplication,
  isRenewalTypeApplication,
  titleStringFromLicenceSubType
} from "../../redux/slice/applicationsUtils";
import { push } from "connected-react-router";

const AgentDetails = (props) => {
  const dispatch = useDispatch();
  const { applicationMode, agentApplicationId } = props.match.params;

  const [agentApplication, setAgentApplication] = useState();
  const [savingApplication, setSavingApplication] = useState(false);
  const [processingError, setProcessingError] = useState(false);
  const [showRemovalConfirmationDialog, setShowRemovalConfirmationDialog] = useState(false);
  const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState(false);

  const applicationType = applicationTypeFromLicenceSubType(agentApplication?.licencingSubType);

  const isAssociateBrokerAppType =
    applicationType === APPLICATION_TYPE_MORTGAGE_ASSOCIATE_BROKER_LICENCE;

  const data = useMemo(() => {
    if (!agentApplication) {
      return null;
    }

    return agentApplication.businessLicenceData;
  }, [agentApplication]);

  const isApproved = useMemo(() => {
    if (!agentApplication) {
      return false;
    }

    const { applicationStatus } = agentApplication;

    if (
      // applicationStatus === APPLICATION_STATUS_WITHDRAWN_APPROVED ||
      // applicationStatus === APPLICATION_STATUS_WITHDRAWN_BY_CLIENT ||
      applicationStatus === APPLICATION_STATUS_DENIED ||
      applicationStatus === APPLICATION_STATUS_PENDING
    ) {
      return false;
    }

    return agentApplication.licenceStatus === LICENCE_STATUS_ACTIVE;
  }, [agentApplication]);

  useEffect(() => {
    dispatch(fetchApplicationWithId(parseInt(agentApplicationId))).then((val) => {
      setAgentApplication(val);
    });
  }, [dispatch, agentApplicationId]);

  const withdrawLicence = () => {
    dispatch(withdrawAgentApplication(parseInt(agentApplicationId)));
    setSavingApplication(true);
    setProcessingError(false);
    const applicationType = applicationTypeFromLicenceSubType(agentApplication?.licencingSubType);
    dispatch(saveBusinessLicenceApplicationData(applicationType, applicationMode))
      .then((data) => {
        setSavingApplication(false);
        props.history.goBack();
      })
      .catch((err) => {
        // could not process requst
        setSavingApplication(false);
        setProcessingError(true);
      });
  };

  const deleteLicence = () => {
    props.dispatch(markAgentApplicationDeleted(parseInt(agentApplicationId)));
    setSavingApplication(true);
    setProcessingError(false);
    const applicationType = applicationTypeFromLicenceSubType(agentApplication?.licencingSubType);
    props
      .dispatch(saveBusinessLicenceApplicationData(applicationType, APPLICATION_MODE_NEW))
      .then((data) => {
        // could not process requst
        setSavingApplication(false);
        props.history.goBack();
      })
      .catch((err) => {
        // could not process request
        // could not process requst
        setSavingApplication(false);
        setProcessingError(true);
      });
  };

  const onClick = () => {};

  const title = useMemo(() => {
    if (!data) {
      return "";
    }
    return `${data.personData.firstName} ${data.personData.lastName}`;
  }, [data]);

  const collectorLicenceData = useMemo(() => {
    if (!data) {
      return [];
    }
    const status = (
      <ApplicationStatusComponent applicationStatus={agentApplication.applicationStatus} />
    );
    const licenceNumber = isApproved ? agentApplication.licenceNumber : "N/A";
    const expiryDate = isApproved ? humanReadableDate(agentApplication.endDate) : "N/A";
    return [
      { header: "Licence status", value: status },
      { header: "Licence number", value: licenceNumber },
      { header: "Expiry date", value: expiryDate }
    ];
  }, [data, isApproved, agentApplication]);

  const contactInformationData = useMemo(() => {
    if (!data) {
      return [];
    }
    return [
      { header: "Home address", value: combineAddress(data.personData.addressData) },
      { header: "Email", value: data.personData.email },
      { header: "Phone", value: data.personData.phoneNumber }
    ];
  }, [data]);

  const criminalHistoryDataRows = useMemo(() => {
    if (!data) {
      return [];
    }
    const { criminalHistoryData } = data;
    if (!criminalHistoryData || criminalHistoryData.length < 1) {
      return [
        [
          {
            header:
              "Has the applicant been convicted for an offence for which they have not been pardoned?",
            value: "No"
          }
        ]
      ];
    }
    return criminalHistoryData.map((criminalHistoryRow) => {
      return [
        {
          header:
            "Has the applicant been convicted for an offence for which they have not been pardoned?",
          value: "Yes"
        },
        { header: "Date of offence", value: humanReadableDate(criminalHistoryRow.dateOfOffence) },

        { header: "Description of offence", value: criminalHistoryRow.description }
      ];
    });
  }, [data]);

  const canCancelApplication = useCallback(() => {
    // MRA-1545 : Mortgage Brokerage - Disable "edit" button on MB and AMB tiles (related to MRA-1529; Assyst ticket 3747211)
    if (
      isMortgageTypeApplication(
        applicationTypeFromLicenceSubType(agentApplication.licencingSubType)
      )
    ) {
      return false;
    }

    return true;
  }, [agentApplication]);

  const canDeleteApplication = useCallback(() => {
    const { applicationStatus } = agentApplication;

    if (
      applicationStatus === APPLICATION_STATUS_UNREAD ||
      applicationStatus === APPLICATION_STATUS_OPENED ||
      applicationStatus === APPLICATION_STATUS_UNDER_REVIEW
    ) {
      return !isRenewalTypeApplication(agentApplication);
    }

    return true;
  }, [agentApplication]);

  const licenceSuspensionHistoryDataRows = useMemo(() => {
    if (!data) {
      return [];
    }
    const { licencesSuspendedData } = data;
    if (!licencesSuspendedData || licencesSuspendedData.length < 1) {
      return [
        [
          {
            header: "Has applicant had a business licence refused, suspended or revoked?",
            value: "No"
          }
        ]
      ];
    }

    return licencesSuspendedData.map((licenceSuspendedData) => {
      return [
        {
          header: "Has applicant had a business licence refused, suspended or revoked?",
          value: "Yes"
        },
        { header: "Type of business licence", value: licenceSuspendedData.businessLicenceType },
        { header: "Province", value: licenceSuspendedData.province },
        { header: "Start date", value: humanReadableDate(licenceSuspendedData.startDate) },
        { header: "End date", value: humanReadableDate(licenceSuspendedData.endDate) }
      ];
    });
  }, [data]);

  const onUpgradeAssociateBrokerClick = () => {
    dispatch(setAssociateBrokerLicenceForUpgrade());
    //setAssociateBrokerLicenceForUpgrade
    dispatch(push(ROUTE_PATH.MRA_APP + ROUTE_PATH.MRA_ASSOCIATE_APPLICANT_INFO));
  };

  if (!agentApplication) {
    return null;
  }

  return (
    <>
      <div className="container">
        <TopNavigationBar {...props} />
        <div className="row bottomMarginMedium">
          <div className="col-lg-8">
            <Header id="collector-details-header" header={title} />
          </div>
        </div>
        {isApproved && (
          <div className="row" style={{ marginBottom: "2rem" }}>
            <div className="col-lg-12">
              <div style={{ display: "flex", flexGrow: 1, backgroundColor: "lightgray" }}>
                <div style={{ width: "5px", backgroundColor: "grey", marginRight: "1rem" }} />
                <Paragraph
                  text={[
                    "Click on Edit to update information or Remove Licence to discontinue and detach this licence from your corporate licence. Once complete, Click Submit your change to Business Licensing team"
                  ]}
                  style={{
                    fontSize: "1.2rem",
                    width: "100%",
                    maxWidth: "100%",
                    marginBottom: "1rem"
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Collector licence */}
        <AgentDetailSection
          header={titleStringFromLicenceSubType(agentApplication.licencingSubType)}
          tableData={collectorLicenceData}
        >
          {isApproved ? (
            <div className={processingError ? " has-error" : ""}>
              <div>
                {isAssociateBrokerAppType && (
                  <Button
                    id="upgrade-to-mb-button"
                    style={{ marginBottom: "20px" }}
                    onClick={onUpgradeAssociateBrokerClick}
                    text={"Eligible for mortgage broker licence"}
                    buttonType="regular_primary"
                  />
                )}

                <Button
                  buttonType="regular_warning"
                  disabled={!canCancelApplication()}
                  showSpinner={savingApplication}
                  text={"Cancel licence"}
                  onClick={() => {
                    setShowRemovalConfirmationDialog(true);
                  }}
                />
                {processingError && ErrorMark()}
              </div>
              {processingError && (
                <div className="help-block">
                  <span className="ns-visually-hidden">Error:</span>
                  {ERROR_STRING.AGENT_DETAILS_REMOVE_ERROR}
                </div>
              )}
            </div>
          ) : (
            <div className={processingError ? " has-error" : ""}>
              <div>
                <Button
                  buttonType="regular_warning"
                  disabled={!canDeleteApplication()}
                  showSpinner={savingApplication}
                  text={"Delete"}
                  onClick={() => {
                    setShowDeleteConfirmationDialog(true);
                  }}
                />
                {processingError && ErrorMark()}
              </div>
              {processingError && (
                <div className="help-block">
                  <span className="ns-visually-hidden">Error:</span>
                  {ERROR_STRING.AGENT_DETAILS_DELETE_ERROR}
                </div>
              )}
            </div>
          )}
        </AgentDetailSection>
        <LineSeperator />

        {/* Contact Information */}
        <AgentDetailSection header={"Contact Information"} tableData={contactInformationData}>
          {isApproved && (
            <div>
              <Button className="btn btn_primary" disabled text={"Edit"} onClick={onClick} />
            </div>
          )}
        </AgentDetailSection>
        <LineSeperator />

        {/* CriminalHistory */}
        {criminalHistoryDataRows.map((criminalHistoryDataRow, index) => (
          <AgentDetailSection
            key={index}
            header={"Criminal History"}
            tableData={criminalHistoryDataRow}
          >
            {isApproved && (
              <div>
                <Button className="btn btn_primary" text={"Edit"} disabled onClick={onClick} />
              </div>
            )}
          </AgentDetailSection>
        ))}
        <LineSeperator />

        {/* Licence Suspension History */}
        {licenceSuspensionHistoryDataRows.map((licenceSuspensionHistoryDataRow, index) => (
          <AgentDetailSection
            key={index}
            header={"History of business licence suspension"}
            tableData={licenceSuspensionHistoryDataRow}
          >
            {isApproved && (
              <div>
                <Button className="btn btn_primary" text={"Edit"} disabled onClick={onClick} />
              </div>
            )}
          </AgentDetailSection>
        ))}

        {/* <LineSeperator /> */}
      </div>

      <Modal
        appElement={document.getElementById("root")}
        isOpen={showRemovalConfirmationDialog || showDeleteConfirmationDialog}
        contentLabel="agentRemovalDialog"
        onRequestClose={() => {
          setShowRemovalConfirmationDialog(false);
          setShowDeleteConfirmationDialog(false);
        }}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        className="agentRemovalDialog"
        closeTimeoutMS={150}
      >
        <div className="row topMarginNormal">
          <div className="col-lg-11">
            <p>
              {`Are you sure you want to ${
                showRemovalConfirmationDialog ? "cancel" : "delete"
              } ${title}'s licence?`}
            </p>
            <p>This action can't be reversed.</p>
          </div>
        </div>
        <LineSeperator />
        <div className="row topMarginNormal buttonsContainer">
          <Button
            className="dialogButton"
            id="agent_removal_yes_button"
            buttonType="regular_warning"
            text={`Yes, ${showRemovalConfirmationDialog ? "cancel" : "delete"} licence`}
            onClick={() => {
              if (showDeleteConfirmationDialog) {
                setShowDeleteConfirmationDialog(false);
                deleteLicence();
              } else if (showRemovalConfirmationDialog) {
                setShowRemovalConfirmationDialog(false);
                withdrawLicence();
              }
            }}
          />

          <NavigationLinkButton
            className="dialogButton"
            id="agent_removal_no_link"
            text="No, keep licence"
            onClick={() => {
              setShowRemovalConfirmationDialog(false);
              setShowDeleteConfirmationDialog(false);
            }}
          />
        </div>
      </Modal>
    </>
  );
};

const AgentDetailSection = ({ header, tableData, children }) => {
  return (
    <div className="row">
      <div className="collector-licence-col col-md-3" style={{ flexGrow: 1, display: "flex" }}>
        <h2>{header}</h2>
      </div>

      <div className="collector-licence-col col-md-7" style={{ display: "flex" }}>
        <AgentDetailsTable rows={tableData} />
      </div>
      <div
        className="collector-licence-action-col col-md-2"
        style={{ flexGrow: 1, display: "flex", justifyContent: "flex-end" }}
      >
        {children}
      </div>
    </div>
  );
};

const AgentDetailsTable = ({ rows }) => {
  return (
    <table style={{ tableLayout: "fixed" }}>
      <tbody>
        {rows.map((row, index) => (
          <tr key={index}>
            <th>{row.header}</th>
            <td style={{ borderTop: "none" }}>
              <span>{row.value}</span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};
export default connect(bltHomeSelect)(AgentDetails);
